import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { likeActivityCall } from '../../services/company_services';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { useAccount } from '../../contexts/Account';
import { AccountType, ActivityType } from '../../services/utils/types';
import { timeAgo } from '../../utils/dateFormat';
import { ReactComponent as LikeHollowIcon } from '../../icons/like-hollow.svg';
import { ReactComponent as LikeIcon } from '../../icons/like.svg';
import DefaultCompany from '../../icons/building_round.svg';
import eventBus, { eventBusValues } from '../../eventBus';

export default function BoardroomActivityCard({ company, activity }) {
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { accountDetails, asLoggedUserDetails, type, isFreemium } =
    useAccount();
  const [likes, setLikes] = useState(activity.likes || []);
  const timeAgoString = timeAgo(activity.createdAt);
  const [isAlreadyLiked, setIsAlreadyLiked] = useState(
    activity.likes
      ? activity.likes.some(
          (element) => element.userId === asLoggedUserDetails.userId
        )
      : false
  );

  function activityLikeAction() {
    if (activity.companyId === accountDetails.id) {
      // setErrorMessage('Not allowed to like your own content');
      // setShowErrorSnackbar(true);
    } else {
      const likeDTO = {
        type:
          type === AccountType.COMPANY
            ? AccountType.CONTACT
            : AccountType.ADVISOR,
        name: asLoggedUserDetails.displayName,
        userId: asLoggedUserDetails.userId,
      };
      addActivityLike(likeDTO);
      likeActivityCall({
        ACCOUNT_TYPE:
          type === AccountType.COMPANY
            ? AccountType.CONTACT
            : AccountType.ADVISOR,
        USER_ID: asLoggedUserDetails.userId,
        ACTIVITY_ID: activity.id,
      });
    }
  }

  function addActivityLike(likeDTO) {
    if (!isAlreadyLiked) {
      setLikes([...likes, likeDTO]);
    } else {
      setLikes(
        likes.filter((element) => element.userId !== asLoggedUserDetails.userId)
      );
    }
    setIsAlreadyLiked(!isAlreadyLiked);
  }

  const getHeaderStyle = () => {
    switch (activity.activityType) {
      case 'advisorJoinedToCompany':
        return {
          backgroundColor: '#D7DAFD',
          color: '#001030',
        };
      case 'advisorIdea':
        return {
          backgroundColor: '#4FB5F9',
          color: 'white',
        };
      case 'surveyResponse':
        return {
          backgroundColor: '#F6F6F6',
          color: '#232B35',
        };
    }
    return 'Activity';
  };
  const headerStyle = getHeaderStyle();
  const getHeaderText = () => {
    switch (activity.activityType) {
      case 'advisorJoinedToCompany':
        return 'New Board Member';
      case 'advisorIdea':
        return `Priority: “${activity.idea?.currentGoal}”`;
      case 'surveyResponse':
        return 'New Survey Response';
    }
    return 'Activity';
  };

  const getAvatarUrl = (activity) => {
    if (activity.activityType === 'surveyResponse') {
      return company?.image?.id
        ? `${process.env.REACT_APP_IMAGES_URL}${company.image.id}`
        : DefaultCompany;
    }
    return `${process.env.REACT_APP_IMAGES_URL}${activity.advisor?.image?.imageKey}`;
  };

  function openProfile(advisorId) {
    if (!advisorId) {
      return;
    }
    eventBus.dispatch(eventBusValues.triggerAdvisorDrawerAction, {
      fetchAdvisor: true,
      id: advisorId,
      boardRoomView: true,
    });
  }

  const getCardBody = () => {
    if (activity.activityType === 'advisorIdea') {
      return activity.idea?.idea;
    } else if (activity.activityType === 'advisorJoinedToCompany') {
      return activity.relationship?.introduction;
    } else if (activity.activityType === 'surveyResponse') {
      return activity.survey?.question;
    }
    return '';
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          borderRadius: '10px',
          bgcolor: 'background.paper',
          mt: 1,
          p: 1,
        }}
      >
        <Box
          sx={{
            ...headerStyle,
            borderRadius: '10px 10px 0 0',
            m: '-8px',
            py: '6px',
            px: '8px',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              // whiteSpace: 'nowrap',
              // overflow: 'hidden',
              // textOverflow: 'ellipsis',
              fontWeight: '600',
              px: isSM ? '20px' : '45px',
            }}
          >
            {getHeaderText()}
          </Typography>
        </Box>
        <Box sx={{ p: '18px', pb: '10px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Avatar
              src={getAvatarUrl(activity)}
              // src={`${process.env.REACT_APP_IMAGES_URL}${activity.advisor.image?.imageKey}`}
              onClick={() =>
                openProfile(activity.advisor?.id || activity.advisorId)
              }
              alt={activity.advisor?.displayName}
              sx={{ width: 40, height: 40, mr: 2, cursor: 'pointer' }}
            />
            {activity.activityType != 'surveyResponse' ? (
              <Box>
                <Typography
                  sx={{
                    fontWeight: '500',
                    color: '#232B35',
                    fontSize: '14px',
                    lineHeight: '32px',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    openProfile(activity.advisor?.id || activity.advisorId)
                  }
                >
                  {activity.advisor?.displayName}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: '500',
                    color: '#74879A',
                    fontSize: '10px',
                    lineHeight: '15px',
                    letterSpacing: '0.75px',
                  }}
                >
                  Advisor
                  {/* Advisor - {timeAgoString} */}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography
                  sx={{
                    fontWeight: '500',
                    color: '#232B35',
                    fontSize: '14px',
                    lineHeight: '32px',
                  }}
                >
                  <Box
                    component="span"
                    onClick={() =>
                      openProfile(activity.advisor?.id || activity.advisorId)
                    }
                  >
                    {activity.survey.userName}
                  </Box>{' '}
                  {`answered ${activity.survey.response}`}
                </Typography>
              </Box>
            )}
          </Box>

          <Typography
            sx={{
              whiteSpace: 'pre-line',
              fontWeight: '400',
              color: '#232B35',
              fontSize: '14px',
              lineHeight: '24px',
            }}
          >
            {getCardBody()}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          py: 0,
          display: 'flex',
          alignItems: 'left',
          cursor: 'pointer',
        }}
        onClick={activityLikeAction}
      >
        <IconButton size="small" aria-label="like">
          {isAlreadyLiked ? (
            <LikeIcon style={{ width: '20px', height: '20px' }} />
          ) : (
            <LikeHollowIcon style={{ width: '20px', height: '20px' }} />
          )}
        </IconButton>
        <Typography
          sx={{
            p: '5px',
            fontSize: '18px',
            fontWeight: '600',
            color: '#232B35',
            letterSpacing: '0.75px',
            lineHeight: '32px',
          }}
        >
          {likes.length} {likes.length === 1 ? 'like' : 'likes'}
        </Typography>
      </Box>
    </>
  );
}
